import { useState } from "react";
import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEvent } from "../../../context/EventContext";
import { storage } from "../../../firebase";
import Loader from "../../Loader/Loader";
import { v4 as uuidv4 } from "uuid";
import defaultProfilePic from "../../../assest/defaultProfile.png";

// Sponsor component is used to add, edit and display sponsor details. It includes
// a form to add/edit sponsor details, and a list of saved sponsors with edit
// buttons. The component also handles the validation of the input data and
// displays an error message if the input is invalid. The component is used in
// the organizer dashboard page.
const Sponsor = ({ onNext }) => {
  const {
    SponsorformData,
    handleSponsorChange,
    sponsorAdded,
    setSponsorAdded,
    loading,
    setLoading,
    formData,
    setSponsorFormData,
    setEditIndex,
    editIndex,
    sponsors,
    setSponsors,
    mainEventID,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);

  //   Handles the "Add Sponsor" button click by setting the isFormVisible state to true,
  //   which displays the sponsor form.
  const handleAddSpace = () => {
    setIsFormVisible(true);
  };

  //   Handles the "Edit" button click by setting the isFormVisible state to true,
  //   which displays the sponsor form. It sets the sponsorFormData state to the
  //   sponsor details at the given index, and sets the editIndex state to the
  //   given index. It also sets the sponsorAdded state to false.
  const handleEdit = (index) => {
    setSponsorFormData(sponsors[index]);
    setIsFormVisible(true);
    setEditIndex(index);
    setSelectedBannerFile(null);
    setSelectedLogoFile(null);
  };

  //   Handles the "Add Sponsor" button click by setting the sponsorFormData state
  //   to an empty object with mainEventID, setting the isFormVisible state to true,
  //   and setting the editIndex state to null. This displays the sponsor form.
  const handleSponsorAdd = () => {
    setSponsorFormData({
      company_name: "",
      sponsor_type: "",
      company_banner: "",
      company_logo: "",
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setEditIndex(null);
  };

  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);

  // Handles the file selection event for the sponsor banner image.
  const handleSponserBannerFile = (e) => {
    setSelectedBannerFile(e.target.files[0]);
  };

  //   Handles the file selection event for the sponsor logo image.
  const handleSponserLogoFile = (e) => {
    setSelectedLogoFile(e.target.files[0]);
  };

  //   Handles the "Delete Logo" button click by setting the selectedLogoFile state
  //   variable to null, effectively deleting the logo image.
  const handleDeleteLogoImage = () => {
    setSelectedLogoFile(null);
  };

  //   Handles the "Delete Banner" button click by setting the selectedBannerFile state
  //   variable to null, effectively deleting the banner image.
  const handleDeleteBannerImage = () => {
    setSelectedBannerFile(null);
  };

  //   Handles the "Save" button click in the sponsor form.
  //   If both a banner and logo are selected, it uploads the images to Firebase Storage
  //   and then sends a POST request to the server to create a new sponsor.
  //   If successful, it shows a success message, sets the isFormVisible state to false, and sets the sponsorAdded state to true.
  //   It also updates the sponsors state with the newly created sponsor.
  //   If the editIndex state is not null, it updates the sponsor at that index in the sponsors state.
  //   Otherwise, it adds the new sponsor to the sponsors state.
  //   If only one of the images is selected, it sends a POST request to the server to update the sponsor.
  //   If only the logo is selected, it updates the logo image of the sponsor.
  //   If only the banner is selected, it updates the banner image of the sponsor.
  //   If neither image is selected, it sends a POST request to the server to create a new sponsor without images.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bannerFile = selectedBannerFile;
      const logoFile = selectedLogoFile;

      if (bannerFile && logoFile) {
        // Correct condition check here
        const bannerID = uuidv4(); // Generate a unique ID for the banner
        const logoID = uuidv4(); // Generate a unique ID for the logo

        const uploadImage = async (file, fileID, setProgress) => {
          const imageRef = storage.ref(
            `${formData.mainEventID}/sponsors/${fileID}`
          );

          const uploadTask = imageRef.put(file);

          return new Promise((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setProgress(progress.toFixed(2));
              },
              (error) => {
                console.error("Error uploading image:", error);
                setLoading(false);
                reject(error);
              },
              async () => {
                const imageUrl = await imageRef.getDownloadURL();
                resolve(imageUrl);
              }
            );
          });
        };

        const bannerImageUrl = await uploadImage(
          bannerFile,
          bannerID,
          setBannerUploadProgress
        );
        const logoImageUrl = await uploadImage(
          logoFile,
          logoID,
          setLogoUploadProgress
        );

        const response = await eventsAPI.post("/organizer/sponsor", {
          ...SponsorformData,
          banner_id: bannerID,
          logo_id: logoID,
          company_banner: bannerImageUrl,
          company_logo: logoImageUrl,
        });

        if (response.status === 200) {
          toast.success("Sponsor added successfully!");
          setLoading(false);
          setIsFormVisible(false);
          setSponsorAdded(true);
          setSponsors([...sponsors, response.data]);

          if (editIndex !== null) {
            const updatedSponsors = sponsors.map((sponsor, index) =>
              index === editIndex ? response.data : sponsor
            );
            setSponsors(updatedSponsors);
          } else {
            setSponsors([...sponsors, response.data]);
          }
        }
      } else {
        const bannerID = uuidv4(); // Generate a unique ID for the banner
        const logoID = uuidv4(); // Generate a unique ID for the logo
        // Handle saving without images
        const response = await eventsAPI.post("/organizer/sponsor", {
          ...SponsorformData,
          banner_id: bannerID,
          logo_id: logoID,
          company_banner: null,
          company_logo: null,
        });

        if (response.status === 200) {
          toast.success("Sponsor added successfully!");
          setLoading(false);
          setIsFormVisible(false);
          setSponsorAdded(true);
          setSponsors([...sponsors, response.data]);

          if (editIndex !== null) {
            const updatedSponsors = sponsors.map((sponsor, index) =>
              index === editIndex ? response.data : sponsor
            );
            setSponsors(updatedSponsors);
          } else {
            setSponsors([...sponsors, response.data]);
          }
        }
      }
    } catch (error) {
      console.error("Error saving sponsor:", error);
      setLoading(false);
    }
  };

  //   * Handles the "Update" button click in the sponsor form.
  //   * Sends a POST request to the server to update a sponsor.
  //   * If successful, it shows a success message, sets the isFormVisible state to false, and updates the local state with the updated sponsor data.
  //   * If the request fails, it shows an error message.
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateSponsor",
        SponsorformData
      );

      if (response.status === 200) {
        toast.success("sponsor updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated location data
        const updatedSponsors = sponsors.map((sponsor, index) =>
          index === editIndex ? response.data : sponsor
        );
        setSponsors(updatedSponsors);
      }
    } catch (error) {
      console.log("Error updating Location:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a sponsor by sending a DELETE request to the server.
  //    * If successful, it removes the sponsor from the local state and shows a success message.
  //    * If the request fails, it shows an error message.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/organizer/sponsor/${id}`); // Pass the location's id

      if (response.status === 200) {
        toast.success("Sponsor deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted location from the local state
        const updatedSponsor = sponsors.filter((sponsor) => sponsor._id !== id);
        setSponsors(updatedSponsor);
      }
    } catch (error) {
      console.log("Error deleting Sponsor:", error);
      setLoading(false);
    }
  };

  return (
    <div className="sponsor-section">
      {!isFormVisible && !sponsorAdded && (
        <button className="add-space" onClick={handleAddSpace}>
          Add Sponsor
        </button>
      )}
      {isFormVisible ? (
        <form onSubmit={handleSave}>
          <h2>Sponsor Details</h2>
          <div className="sponsor-details">
            <label htmlFor="sponsor-name">Company Name</label>
            <input
              value={SponsorformData.company_name}
              onChange={handleSponsorChange}
              //required
              name="company_name"
              type="text"
              placeholder="Enter the name of your sponsor"
            />
            <label htmlFor="sponsor-type">Sponsorship Level</label>
            <select
              name="sponsor_type"
              value={SponsorformData.sponsor_type}
              onChange={handleSponsorChange}
              //required
            >
              <option value="select">select</option>
              <option value="diamond">Diamond</option>
              <option value="platinum">Platinum</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="bronze">Bronze</option>
            </select>
            <label className="upload-label" htmlFor="upload-banner">
              Upload Banner Image
            </label>
            <input
              onChange={handleSponserBannerFile}
              //required
              name="company_banner"
              type="file"
              id="upload-banner"
              className="upload-input"
            />

            {selectedBannerFile ? (
              <img
                src={URL.createObjectURL(selectedBannerFile)}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            ) : SponsorformData.company_banner ? (
              <img
                src={SponsorformData.company_banner}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            ) : (
              <img
                src={defaultProfilePic}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            )}
            <button
              type="button"
              className="add-space"
              onClick={handleDeleteBannerImage}
            >
              Delete
            </button>

            {bannerUploadProgress > 0 && (
              <div className="progress-bar">
                <div
                  className="progress"
                  style={{
                    width: `${bannerUploadProgress}%`,
                    backgroundColor: "green",
                    color: "white",
                  }}
                >
                  {bannerUploadProgress}%
                </div>
              </div>
            )}
            <p className="upload-info">Valid file formats: JPG, GIF, PNG.</p>
            <label className="upload-label" htmlFor="upload-logo">
              Upload Company Logo
            </label>
            <input
              onChange={handleSponserLogoFile}
              //required
              name="company_logo"
              type="file"
              id="upload-logo"
              className="upload-input"
            />
            {selectedLogoFile ? (
              <img
                src={URL.createObjectURL(selectedLogoFile)}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            ) : SponsorformData.company_logo ? (
              <img
                src={SponsorformData.company_logo}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            ) : (
              <img
                src={defaultProfilePic}
                alt="Profile Pic"
                style={{ height: "60px", width: "60px", objectFit: "cover" }}
              />
            )}
            <button
              type="button"
              className="add-space"
              onClick={handleDeleteLogoImage}
            >
              Delete
            </button>
            {logoUploadProgress > 0 && (
              <div className="progress-bar">
                <div
                  className="progress"
                  style={{
                    width: `${logoUploadProgress}%`,
                    backgroundColor: "green",
                    color: "white",
                  }}
                >
                  {logoUploadProgress}%
                </div>
              </div>
            )}
            <p className="upload-info">Valid file formats: JPG, GIF, PNG.</p>
            {/* Show the "Save" button if adding a new location, and "Update" button if editing */}
            {editIndex === null ? (
              <button
                type="submit"
                className="create-button"
                onClick={handleSave}
              >
                {loading ? <Loader /> : "SAVE"}
              </button>
            ) : (
              <>
                <button
                  type="submit"
                  className="create-button"
                  onClick={handleUpdate}
                >
                  {loading ? <Loader /> : "UPDATE"}
                </button>
                <button
                  type="submit"
                  className="create-button"
                  onClick={(e) => handleDelete(e, SponsorformData._id)}
                >
                  {loading ? <Loader /> : "DELETE"}
                </button>
              </>
            )}
          </div>
        </form>
      ) : (
        sponsorAdded && (
          <>
            {sponsors.map((sponsor, index) => (
              <div className="saved-details" key={index}>
                <p>
                  <span>Company Name: </span> {sponsor.company_name}
                </p>
                <p>
                  <span>Sponsorship Level:</span> {sponsor.sponsor_type}
                </p>
                <button className="add-space" onClick={() => handleEdit(index)}>
                  Edit
                </button>
              </div>
            ))}
            <div className="buttons-controller">
              <button className="add-space" onClick={onNext}>
                Next
              </button>
              <button className="add-space" onClick={handleSponsorAdd}>
                Add
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default Sponsor;
